import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { ClipCategory, ClipLabelVocabulary } from 'services/VideoEditingService';
import ClipLabelFields, { Draft } from './ClipLabelFields';

interface ClipLabelsDialogProps {
  draft: Draft;
  vocabulary: ClipLabelVocabulary | null;
  categories?: ClipCategory[] | null;
  onCreateCategory?: (name: string) => Promise<ClipCategory>;
  showConfidence?: boolean;
  title?: string;
  // Called with the edited draft on Done. Nothing is saved to the server here — the parent
  // decides when to persist, so this and the range can still go up in one request.
  onDone: (draft: Draft) => void;
  onCancel: () => void;
}

/**
 * The label fields, in their own modal.
 *
 * Exists so the dialog that owns them stays short: a trimmer plus eight always-open fields
 * doesn't fit a screen, and the alternative — an inline pencil — is what made pressing "Edit"
 * show a read-only summary.
 *
 * Edits a COPY of the draft and only lifts it on Done, so Cancel genuinely discards rather than
 * leaving half-typed values behind in the parent.
 */
export default function ClipLabelsDialog({
  draft, vocabulary, categories, onCreateCategory, showConfidence, title, onDone, onCancel,
}: ClipLabelsDialogProps) {
  const [working, setWorking] = useState<Draft>(draft);

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {title || 'Edit clip details'}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Nothing is saved until you save the clip itself.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <ClipLabelFields
          draft={working}
          onDraftChange={setWorking}
          vocabulary={vocabulary}
          categories={categories}
          onCreateCategory={onCreateCategory}
          showConfidence={showConfidence}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => onDone(working)}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
